import { LogisticsProjectLookupComponent } from '../logistics-projects/logistics-project-lookup.component';
import { Component, OnInit } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { Router, RouterModule } from "@angular/router";
import { CustomsClearanceHeader } from "./models/customs-clearance-header.model";
import { CustomsClearanceEndpointService } from "./clearance.service";
import { SessionStorageService } from "../services/session-storage.service";
import { CommonModule } from '@angular/common';
import { PermissionService } from '../security/permission.service';
import { app_permissions } from '../security/app-permissions';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ProgressBarModule } from "primeng/progressbar";
import { DataViewModule } from "primeng/dataview";
import { ImageModule } from "primeng/image";
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';

@Component({
    selector: 'app-clearance',
    templateUrl: './clearance.component.html',
    styleUrls: ['./clearance.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        RouterModule,
        ToastModule,
        ButtonModule,
        TableModule,
        ToolbarModule,
        BreadcrumbModule,
        InputTextModule,
        CardModule,
        TagModule,
        ProgressBarModule,
        DataViewModule,
        ImageModule,
        FormsModule,
        SelectModule,
        DatePickerModule,
        PageShellComponent,
        PageHeroComponent,
        PageDataPanelComponent
    ],
    providers: [
        MessageService,
        CustomsClearanceEndpointService,
        SessionStorageService
    ]
})
export class ClearanceComponent implements OnInit {

    canViewClearance = false;
    canCreateClearance = false;
    canEditClearance = false;

    breadcrumbItems: MenuItem[] | undefined;
    homeBreadcrumb: MenuItem | undefined;

    clearances: CustomsClearanceHeader[] = [];
    isLoading: boolean = true;
    first = 0;
    rows = 6;
    totalRecords = 0;
    statusFilter: string | null = null;
    brokerFilter = '';
    portFilter = '';
    etaFrom: Date | null = null;
    etaTo: Date | null = null;
    selectedLogisticsProjectId: number | null = null;

    statusOptions = [
        { label: 'All statuses', value: null },
        { label: 'Draft', value: 'Draft' },
        { label: 'Docs Review', value: 'Docs Review In Progress' },
        { label: 'Customs Filing', value: 'Customs Filing In Progress' },
        { label: 'Inspection', value: 'Customs Inspection' },
        { label: 'Released / Delivery', value: 'Out for Delivery' },
        { label: 'Completed', value: 'Completed' }
    ];

    constructor(
        private clearanceService: CustomsClearanceEndpointService,
        private messageService: MessageService,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService
    ) {
    }

    ngOnInit(): void {
        this.canViewClearance = this.permissionService.hasPermission(app_permissions.clearance.records.view);
        this.canCreateClearance = this.permissionService.hasPermission(app_permissions.clearance.records.create);
        this.canEditClearance = this.permissionService.hasPermission(app_permissions.clearance.records.edit);
        this.breadcrumbItems = [
            { label: 'Clearance' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        this.loadData();
    }

    loadData(event?: any) {
        if (!this.canViewClearance) {
            this.isLoading = false;
            return;
        }
        this.isLoading = true;
        this.first = event?.first ?? 0;
        const pageSize = event?.rows ?? this.rows;
        const pageNumber = event ? Math.floor(event.first / pageSize) : 0;

        this.clearanceService.getAllCustomsClearanceHeadersByCompany(
            this.sessionStorageService.getCompany()?.id ?? 0,
            pageNumber, pageSize, "clearanceHeaderID", "DESC", this.selectedLogisticsProjectId).subscribe({
                next: (data) => {
                    this.clearances = data.content;
                    this.totalRecords = data.totalElements;
                    this.isLoading = false;
                },
                error: (error) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Error loading clearances'
                    });
                    this.isLoading = false;
                }
            });
    }

    addClearance() {
        if (!this.canCreateClearance) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to create clearances.' });
            return;
        }
        this.router.navigate(['/clearance/add'], {
            queryParams: this.selectedLogisticsProjectId ? { projectId: this.selectedLogisticsProjectId } : undefined
        });
    }

    editClearance(clearance: CustomsClearanceHeader) {
        if (!this.canEditClearance) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to edit clearances.' });
            return;
        }
        this.router.navigate(['/clearance/edit', clearance.clearanceHeaderID]);
    }

    viewClearance(clearance: CustomsClearanceHeader) {
        this.router.navigate(['/clearance/view', clearance.clearanceHeaderID]);
    }

    openProject(clearance: CustomsClearanceHeader): void {
        if (!clearance.logisticsProjectId) {
            return;
        }
        this.router.navigate(['/logistics-projects', clearance.logisticsProjectId]);
    }

    get visibleClearances(): CustomsClearanceHeader[] {
        return this.clearances.filter(clearance => {
            const statusMatch = !this.statusFilter || clearance.status === this.statusFilter;
            const broker = clearance.broker?.name?.toLowerCase() || '';
            const brokerMatch = !this.brokerFilter || broker.includes(this.brokerFilter.toLowerCase());
            const port = `${clearance.pod || ''} ${clearance.customsLocation || ''}`.toLowerCase();
            const portMatch = !this.portFilter || port.includes(this.portFilter.toLowerCase());
            const eta = clearance.eta ? new Date(clearance.eta).getTime() : null;
            const etaFromMatch = !this.etaFrom || (eta !== null && eta >= this.etaFrom.getTime());
            const etaToMatch = !this.etaTo || (eta !== null && eta <= this.etaTo.getTime());
            return statusMatch && brokerMatch && portMatch && etaFromMatch && etaToMatch;
        });
    }

    isPreClearanceLate(clearance: CustomsClearanceHeader): boolean {
        if (!clearance.eta || clearance.status === 'Completed' || clearance.status === 'Cancelled') return false;
        const deadline = new Date(clearance.eta).getTime() - 48 * 60 * 60 * 1000;
        return Date.now() > deadline && !`${clearance.status}`.includes('Filing') && clearance.status !== 'Delivered';
    }

    getSeverity(status: string): string {
        switch (status?.toUpperCase()) {
            case 'COMPLETED':
                return 'success';
            case 'PROCESSING':
            case 'CUSTOMS FILING IN PROGRESS':
            case 'CUSTOMS INSPECTION':
                return 'info';
            case 'PENDING':
                return 'warning';
            case 'CANCELLED':
                return 'danger';
            default:
                return 'secondary';
        }
    }

}
